import { computed, ref } from "vue";
import type {
  Document,
  DocumentSubmission,
  DocumentSubmissionEntityType,
  DocumentSubmissionStatus,
  DocumentSubmissionType,
} from "../../../../types";
import { supabase } from "../lib/supabase";

type LatestSubmission = DocumentSubmission | null;

function getDocumentTypeForEntity(entityType: DocumentSubmissionEntityType): Document["type"] {
  return entityType === "offerte" ? "offerte" : "offerte_bundel";
}

export function useDocumentSubmissions(entityType: DocumentSubmissionEntityType, entityId: string) {
  const loading = ref(false);
  const currentDocument = ref<Document | null>(null);
  const latestSubmission = ref<LatestSubmission>(null);
  const error = ref<string | null>(null);

  const canUpload = computed(() => currentDocument.value?.status === "completed");
  const isAppSignedSubmission = computed(() => latestSubmission.value?.submission_type === "app_signed_pdf");
  const isManualSignedSubmission = computed(() => latestSubmission.value?.submission_type === "manual_signed_pdf");
  const requiresRedownload = computed(() => {
    if (!latestSubmission.value) return false;
    return latestSubmission.value.status === "invalidated" || currentDocument.value?.status !== "completed";
  });

  async function load() {
    if (!entityId) return;

    loading.value = true;
    error.value = null;

    try {
      const documentType = getDocumentTypeForEntity(entityType);
      const baseDocumentQuery = supabase
        .from("documents")
        .select("*")
        .eq("type", documentType)
        .order("updated_at", { ascending: false })
        .limit(1);

      const documentResult =
        entityType === "offerte"
          ? await baseDocumentQuery.eq("offerte_id", entityId).maybeSingle()
          : await baseDocumentQuery.eq("bundel_id", entityId).maybeSingle();

      if (documentResult.error) throw documentResult.error;
      currentDocument.value = (documentResult.data as Document | null) ?? null;

      const submissionResult = await supabase
        .from("document_submissions")
        .select("*")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (submissionResult.error) throw submissionResult.error;
      latestSubmission.value = (submissionResult.data as LatestSubmission) ?? null;
    } catch (err) {
      console.error("Error loading document submission state:", err);
      error.value = err instanceof Error ? err.message : "Onbekende fout bij laden documentstatus";
    } finally {
      loading.value = false;
    }
  }

  async function getPdfSignedUrl(downloadFileName?: string): Promise<string | null> {
    if (!currentDocument.value?.pdf_path) {
      return null;
    }

    const { data, error: signedUrlError } = await supabase.storage
      .from("generated-docs")
      .createSignedUrl(currentDocument.value.pdf_path, 3600, downloadFileName ? { download: downloadFileName } : undefined);

    if (signedUrlError) {
      throw signedUrlError;
    }

    return data?.signedUrl || null;
  }

  async function createSubmission(filePath: string, fileName?: string, mimeType?: string) {
    if (!currentDocument.value?.id) {
      throw new Error("Geen actueel document beschikbaar");
    }

    const { data, error: submissionError } = await supabase.rpc("create_document_submission", {
      p_document_id: currentDocument.value.id,
      p_entity_type: entityType,
      p_entity_id: entityId,
      p_submission_type: "manual_signed_pdf",
      p_uploaded_file_path: filePath,
      p_uploaded_file_name: fileName,
      p_uploaded_mime_type: mimeType,
    });

    if (submissionError) {
      throw submissionError;
    }

    latestSubmission.value = (data as DocumentSubmission) || null;
    return latestSubmission.value;
  }

  function getSubmissionLabel(submissionOrStatus: DocumentSubmission | DocumentSubmissionStatus | null | undefined): string {
    const submission =
      submissionOrStatus && typeof submissionOrStatus === "object" ? (submissionOrStatus as DocumentSubmission) : null;
    const status = submission?.status ?? (submissionOrStatus as DocumentSubmissionStatus | null | undefined);

    switch (status) {
      case "pending_review":
        return "Wacht op controle";
      case "approved":
        if (submission?.submission_type === "app_signed_pdf") return "Direct ondertekend";
        return submission?.reviewed_at ? "Goedgekeurd" : "Beschikbaar";
      case "rejected":
        return "Afgekeurd";
      case "invalidated":
        return "Opnieuw downloaden vereist";
      default:
        return "Nog geen upload";
    }
  }

  function getSubmissionTitle(submissionType: DocumentSubmissionType | null | undefined): string {
    return submissionType === "app_signed_pdf" ? "In-app getekende PDF" : "Handmatig getekende PDF";
  }

  return {
    loading,
    currentDocument,
    latestSubmission,
    error,
    canUpload,
    isAppSignedSubmission,
    isManualSignedSubmission,
    requiresRedownload,
    load,
    getPdfSignedUrl,
    createSubmission,
    getSubmissionLabel,
    getSubmissionTitle,
  };
}
